import {_, action, computed, observable, React, utils, Fragment} from '@api'
import {ImageWrapperDefinition} from '@api/graphql'
import {Toast, Toaster} from '@blueprintjs/core'
import {CatalogItemSidebarDetailsStore} from '@modules'
import {ActionButtonsConfig, SidebarStore, StoresInstance, ui} from '@stores'
import {CatalogInstanceChildStore, CatalogStore, isOfflineId, OFFLINE_ID} from './'
import * as css from './CatalogDialogsStore.css'

export interface IAdditionalData {
  type?: string,
  index?: string,
  name?: string,
  targetIds?: Array<string>,
  targetIndex?: string,
  tagPath?: string
}

export type ImageAction = 'DELETE' | 'UPLOAD' | 'REORDER'

/**
 * Cannot be changed without changing existing graphql mutation
 */
export interface IImageOperationInput {
  index: string
  entityExId?: string
  imageId?: string
  imagePosition?: number
  imageAction: ImageAction
  image?: File
  originalImageId?: string
  replacementImageId?: string
  esImageId?: string
  catalogItemId?:string
  images?: Array<{ exId: string }>
}

export type Board = 'designBoard' | 'merchBoard' | string | null

export interface ConfirmationPopup {
  catalogIndex?: string
  catalog?: CatalogStore
  details?: CatalogItemSidebarDetailsStore
  id?: string
  show?: boolean
  operation?: ConfirmationPopupOperation
  message?: React.ReactNode,
  skipConfirm?: boolean
  inProgress?: boolean
  imageId?: string,
  onSuccess?: Function,
  additionalData?: IAdditionalData
  children?: React.ReactNode
  confirmationLabel?: string
  onConfirm?: Function
  canConfirm?: boolean
  onCancel?: () => void
  canCancel?: boolean
  image?: ImageWrapperDefinition
}

export type ConfirmationPopupOperation = 'DELETE_PRODUCT' | 'DUPLICATE_PRODUCT' | 'DUPLICATE_PRODUCTS' | 'DELETE_PRODUCTS' | 'DELETE_IMAGE' | 'UPLOAD_IMAGE' | 'REORDER_IMAGE' | 'UPLOAD_PRODUCT' | 'SYNC_PRODUCT' | 'DELETE_TAG' | 'UPDATE_TAG' | 'DUPLICATE_SIDEBAR_ITEM' | 'DUPLICATE_SINGLE_PRODUCT' | 'REPLACE_PRODUCT'

export class CatalogDialogsStore extends CatalogInstanceChildStore {
  constructor(catalog) {
    super(catalog)
  }

  @observable snackbar: {
    align: { vertical: 'top' | 'bottom', horizontal: 'left' | 'center' | 'right' },
    message: string | React.ReactNode,
    show: boolean
  } = {
    align: {vertical: 'bottom', horizontal: 'right'},
    message: '',
    show: false
  }

  @observable confirmationPopup: ConfirmationPopup = {
    id: null,
    show: false,
    operation: null,
    message: '',
    inProgress: false
  }

  @action onCloseSnackbar = () => {
    _.assign(this.snackbar, {show: false, message: ''})
  }

  getDataForIds = async (ids, comment): Promise<Array<any>> => {
    if (_.isEmpty(ids)) return []

    const {es, catalog} = this
    const {hits: {hits: data}} = await es.client.search({
      index: catalog.index,
      meta: comment,
      body: {
        query: {
          terms: {
            exId: ids
          }
        }
      }
    })

    return data
  }

  @action onConfirmPopup = async () => {
    const {confirmationPopup, confirmationPopup: {operation, id, imageId, additionalData, onConfirm}, site: {onError}} = this,
      catalog = confirmationPopup.catalog ?? this.catalog,
      details = confirmationPopup.details ?? catalog.sidebar?.right?.details,
      {
        refetch,
        features: {actionButtons},
        selection: {
          deleteSelectedItems
        },
        mutations: {
          deleteItem,
          catalogImageOperation,
          deleteCatalogTag,
          updateCatalogTag
          // duplicateSelectedProducts,
          // selectedCatalogItemIds,
          // addToSelected
        },
        filter
      } = catalog,
      catalogIndex = confirmationPopup.catalogIndex ?? catalog.index

    //console.debug(`confirmPopup()`, {operation, id})

    try {
      confirmationPopup.inProgress = true
      confirmationPopup.show = false

      let result
      switch (operation) {
        case 'DELETE_PRODUCT':
          result = await deleteItem(id)

          if ((details?.data?._id == id || details?.data?.id == id)) {
            details?.clearRightSidebarDetails()
          }

          await refetch()
          return result
        case 'DUPLICATE_PRODUCT':
          // const newProduct = await duplicateProduct(id)
          // runInAction(`Select newly created duplicate product`, () => {
          //   if (newProduct) {
          //     selected.clear()
          //     if (addToSelected) {
          //       // Set duplicated product as selected only in case custom attribute filer is
          //       // not applied
          //       selected.add(newProduct.id)
          //     }
          //   }
          //   this.onCancelPopup()
          //   runInAction(() => {
          //     stores.product.sidebarUpdated = true
          //     stores.product.sidebarAction  = 'duplicate'
          //   })
          // })
          // return newProduct
          return null
        case 'DUPLICATE_SINGLE_PRODUCT':
          const {catalog: {createItem: {_duplicateCatalogItemById: duplicateSingleCatalogItem}, selection, selection: {duplicateItemId}}} = this
          await duplicateSingleCatalogItem({index: catalogIndex, id: duplicateItemId})
          selection.duplicateItemId = null
          return result
        case 'DUPLICATE_PRODUCTS':
          const {catalog: {createItem: {_duplicateCatalogItemById}, selection: {selectedIds: sIds}}} = this,
            selectedIds = Array.from(sIds).map(id => id.slice(id.lastIndexOf('.') + 1)),
            onlineIds = selectedIds.filter(id => !id.startsWith(OFFLINE_ID)),
            offlineIds = _.difference(selectedIds, onlineIds),
            onlineIdsESData = await this.getDataForIds(onlineIds, `Get data for selected items`),
            onlineIdsData = _.map(onlineIdsESData, d => ({...d._source})),
            onlineIdsDataKeyBy = _.keyBy(onlineIdsData, 'exId')
          if (_.isObject(actionButtons?.duplicate)) {
            const duplicateConfigObj = actionButtons?.duplicate as ActionButtonsConfig
            if (duplicateConfigObj?.isEnabled && _.isFunction(duplicateConfigObj?.isEnabled)) {
              const userPermissibleData = _.reduce(onlineIdsDataKeyBy, (result, value, key) => {
                  if (catalog.isActionButtonEnabled('duplicate', value)) {
                    result[key] = value
                  }
                  return result
                }, {}),
                itemsToDuplicate = ([]).concat(_.keys(userPermissibleData)).concat(offlineIds)
              itemsToDuplicate.forEach(async id => {
                await _duplicateCatalogItemById({index: catalogIndex, id, selectedItemData: onlineIdsDataKeyBy[id]})
              })
            }
          } else {
            selectedIds.forEach(async id => {
              await _duplicateCatalogItemById({index: catalogIndex, id, selectedItemData: onlineIdsDataKeyBy[id]})
            })
          }
          sIds.clear()
          //result = await duplicateSelectedProducts(null, {connectionWhere: gql.whereProductsClause})
          return result
        case 'DUPLICATE_SIDEBAR_ITEM':
          const {catalog: {createItem: {_duplicateCatalogItemById: duplicateCatalogItemById}}} = this
          await duplicateCatalogItemById({index: catalogIndex, id})
          return result
        case 'DELETE_PRODUCTS':
          result = await deleteSelectedItems()
          await refetch()
          return result
        case 'DELETE_IMAGE':
          const {data, isOffline, updateFieldData, flexLayout: {refetchAllCatalogRelatedTabs}} = details, {image} = confirmationPopup
          if (!isOffline) {
            console.log('Delete entity image details', {id, imageId, image})
            if (id) {
              const imageOperationInput: IImageOperationInput = {
                entityExId: id, // entityExId is String!
                imageId,
                index: catalogIndex ?? catalog.index,
                imageAction: 'DELETE'
              }

              // const images: Array<ImageWrapperDefinition> = localImages.map(v => _.omit(v, ['ws', '__parent'])),
              //   imageIdx = _.findIndex(images, img => img?.exId == id)
              // images.splice(imageIdx, 1)
              // updateFieldData('images', images)
              await catalogImageOperation(imageOperationInput)

              refetchAllCatalogRelatedTabs()
              StoresInstance.allStores.forEach(({catalog}) => {
                if (catalog.isDataTableView && catalog.index === catalogIndex) catalog._tableRefreshDataByIds = [id]
              })

            } else {
              onError(`All parameters are required to delete image id: ${id} , Catalog item ${id} - ${catalogIndex}`)
            }
          }
          break
        case 'UPLOAD_IMAGE':
          // api call to upload image
          console.log(id)
          break
        case 'REORDER_IMAGE':
          // api call to reorder image

          console.log(id)
          break
        case 'UPLOAD_PRODUCT':
          await details.saveCreateItem()
          break
        case 'SYNC_PRODUCT':
          details.onSyncDetails(id)
          break
        case 'DELETE_TAG':
          await deleteCatalogTag({id, ...additionalData})
          break
        case 'UPDATE_TAG':
          await updateCatalogTag({id, ...additionalData})
          break
        default:
          onConfirm && onConfirm()
          break
      }
    } catch (err) {
      onError(err)
    } finally {
      confirmationPopup.inProgress = false
      confirmationPopup.onSuccess && confirmationPopup.onSuccess()
      confirmationPopup.onSuccess = null
      setTimeout(async () => {
        await Promise.all([
          await this.flexLayout.refetchAllCatalogRelatedTabs()
        ])
      }, 0)
    }
  }

  /** QR Codes on a card-wide basis */
  @observable showQrCodes: boolean = false
  @action toggleShowQrCodes = () => {
    this.showQrCodes = !this.showQrCodes
  }

  /** QR Code for the current page */
  @observable showQrCodeModal = false

  @computed get qrCodeUrl() {
    const {stores: {nav}, workspaces} = this
    let url = nav.urlFor_string({workspace: workspaces.currentConfig})
    url += `?${utils.stringifyQs(_.omit(this.stores.nav.queryParams, ['f', 'cardView', 'zoom', 'specZoom']))}`
    return url
  }

  @action showConfirmationProductPopup = (params: ConfirmationPopup) => {
    this.confirmationPopup = {show: true, ...params}
  }

  @action onCancelPopup = () => {
    const {confirmationPopup: {onCancel}} = this
    this.confirmationPopup = {show: false, id: null, message: '', operation: null}
    onCancel && onCancel()
  }

  @action showToastMessage = (message: string | React.ReactNode) => {
    _.assign(this.snackbar, {message: _.isString(message) ? <div className={css.message}> {message} </div> : message, show: true})
  }

  @action showErrorMessage = (message: string, align: { vertical: 'top' | 'bottom', horizontal: 'left' | 'right' } = {vertical: 'bottom', horizontal: 'right'}) => {
    _.assign(this.snackbar, {message, show: true, align})
    //const {sidebar: {right: }} = this
    //this.itemDetailsStore.mutationData = {}
    // TODO : autoHideDuration on snackBar is not working with observable mobx flags
    setTimeout(action(() => {
      this.onCloseSnackbar()
      this.snackbar.align = {vertical: 'bottom', horizontal: 'right'}
    }), ui.settings.AUTOHIDE_SNACKBAR_DURATION)
  }

  @action showSuccessMessage = (message: string, align: { vertical: 'top' | 'bottom', horizontal: 'left' | 'right' } = {vertical: 'bottom', horizontal: 'right'}) => {
    _.assign(this.snackbar, {message, show: true, align})
    // TODO : autoHideDuration on snackBar is not working with observable mobx flags
    setTimeout(action(() => {
      this.onCloseSnackbar()
      this.snackbar.align = {vertical: 'bottom', horizontal: 'right'}
    }), ui.settings.AUTOHIDE_SNACKBAR_DURATION)
  }
}


// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogDialogsStore.tsx